/**
 * The Omega Wolf Guide inside the Knowledgebase Hub (Jon, 2026-11-03): how Omega
 * Wolf and Lupa work, in plain language, from app/docs/guide (always the deployed
 * version). Contents on the left, the page on the right. Holders of the Technical
 * permission also get each page's "Technical details" (the developer Knowledge
 * Bank). KB omega-wolf-guide.md.
 */
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faBookOpen, faChevronDown, faChevronRight, faWrench } from '@fortawesome/free-solid-svg-icons';
import { DWA } from '../theme/dwaTokens';
import { fetchGuidePage, fetchGuideToc, searchGuide, type GuideHit, type GuidePage, type GuideToc } from '../services/kbApi';
import { renderMarkdown } from './miniMarkdown';

export const GUIDE_START = '00-quick-start';

const card: React.CSSProperties = {
  background: DWA.cardBg,
  borderRadius: DWA.radius,
  boxShadow: DWA.cardShadow,
  color: DWA.ink,
  minWidth: 0,
};

const navButton: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: 'none',
  padding: '0.35rem 0.5rem',
  borderRadius: DWA.radius,
  fontFamily: DWA.font,
  fontSize: '0.85rem',
  cursor: 'pointer',
  color: DWA.ink,
};

/** Guide pages matching a Knowledgebase search, shown above the SOP results. */
export const GuideHits: React.FC<{ hits: GuideHit[]; onOpen: (slug: string) => void }> = ({ hits, onOpen }) =>
  hits.length ? (
    <section aria-label="From the Omega Wolf Guide" data-testid="guide-hits" style={{ marginTop: '1.25rem' }}>
      <p style={{ margin: '0 0 0.5rem', color: DWA.onDarkMuted, fontSize: '0.85rem' }}>From the Omega Wolf Guide</p>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {hits.slice(0, 3).map((h) => (
          <button key={h.slug} type="button" onClick={() => onOpen(h.slug)} style={{ ...card, border: 'none', textAlign: 'left', padding: '0.75rem 1rem', cursor: 'pointer', fontFamily: DWA.font }}>
            <span style={{ fontWeight: 700 }}>{h.title}</span>
            <span style={{ fontSize: '0.75rem', color: DWA.textMuted }}> · {h.section}</span>
            <span style={{ display: 'block', fontSize: '0.82rem', color: DWA.textSecondary, marginTop: '0.2rem' }}>{h.snippet}</span>
          </button>
        ))}
      </div>
    </section>
  ) : null;

export const GuideView: React.FC<{ slug: string; onOpen: (slug: string) => void; onClose: () => void }> = ({ slug, onOpen, onClose }) => {
  const [toc, setToc] = useState<GuideToc | null>(null);
  const [page, setPage] = useState<GuidePage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTech, setShowTech] = useState(false);
  // Search within the guide only (the Knowledgebase search box isn't shown here).
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<GuideHit[] | null>(null);
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) {
      setHits(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      searchGuide(q)
        .then((r) => !cancelled && setHits(r.results))
        .catch(() => !cancelled && setHits([]));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [term]);

  useEffect(() => {
    fetchGuideToc().then(setToc).catch(() => setToc({ sections: [] }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setShowTech(false);
    fetchGuidePage(slug)
      .then((p) => !cancelled && setPage(p))
      .catch((e: Error) => !cancelled && setError(e.message));
    window.scrollTo?.(0, 0);
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div data-testid="guide-view" style={{ marginTop: '1.25rem' }}>
      <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', color: DWA.gold, cursor: 'pointer', fontFamily: DWA.font, fontSize: '0.85rem', padding: 0 }}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Knowledgebase
      </button>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        <nav aria-label="Guide contents" style={{ ...card, flex: '1 1 220px', maxWidth: 300, padding: '0.85rem' }}>
          <p style={{ margin: '0 0 0.5rem', fontWeight: 700, fontSize: '0.9rem' }}>
            <FontAwesomeIcon icon={faBookOpen} style={{ color: DWA.goldInk, marginRight: '0.4rem' }} />
            Omega Wolf Guide
          </p>
          <input
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search the guide"
            aria-label="Search the Omega Wolf Guide"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              margin: '0 0 0.6rem',
              padding: '0.45rem 0.6rem',
              borderRadius: DWA.radius,
              border: `1px solid ${DWA.lineLight}`,
              fontFamily: DWA.font,
              fontSize: '0.85rem',
            }}
          />
          {hits && (
            <div data-testid="guide-search-results" style={{ marginBottom: '0.6rem' }}>
              {hits.length === 0 && <p style={{ margin: 0, fontSize: '0.8rem', color: DWA.textSecondary }}>No guide pages match.</p>}
              {hits.map((h) => (
                <button
                  key={h.slug}
                  type="button"
                  onClick={() => {
                    setTerm('');
                    onOpen(h.slug);
                  }}
                  style={{ ...navButton, padding: '0.4rem 0.5rem' }}
                >
                  <span style={{ display: 'block', fontWeight: 700 }}>{h.title}</span>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: DWA.textSecondary, lineHeight: 1.4 }}>{h.snippet}</span>
                </button>
              ))}
            </div>
          )}
          {!hits && toc?.sections.map((s) => (
            <div key={s.name} style={{ marginBottom: '0.6rem' }}>
              <p style={{ margin: '0 0 0.2rem', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: DWA.textSecondary }}>{s.name}</p>
              {s.pages.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  aria-current={p.slug === slug ? 'page' : undefined}
                  onClick={() => onOpen(p.slug)}
                  style={{ ...navButton, fontWeight: p.slug === slug ? 700 : 400, background: p.slug === slug ? DWA.goldSoft : 'none' }}
                >
                  {p.title}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <article className="ow-guide" style={{ ...card, flex: '3 1 420px', padding: 'clamp(1rem, 3vw, 1.75rem)' }}>
          {error && <p role="alert" style={{ color: DWA.danger }}>Couldn't load this page: {error}</p>}
          {!page && !error && <p style={{ color: DWA.textSecondary }}>Loading…</p>}
          {page && (
            <>
              <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: DWA.goldInk }}>{page.section}</p>
              <div data-testid="guide-body">{renderMarkdown(page.body.replace(/^#\s+.*\n?/, `# ${page.title}\n`), onOpen)}</div>

              {page.technical && page.technical.length > 0 && (
                <section data-testid="guide-technical" style={{ marginTop: '1.5rem', borderTop: `1px solid ${DWA.lineLight}`, paddingTop: '1rem' }}>
                  <button type="button" aria-expanded={showTech} onClick={() => setShowTech((v) => !v)} style={{ ...navButton, fontWeight: 700, padding: 0 }}>
                    <FontAwesomeIcon icon={showTech ? faChevronDown : faChevronRight} style={{ marginRight: '0.4rem', fontSize: '0.75rem' }} />
                    <FontAwesomeIcon icon={faWrench} style={{ marginRight: '0.4rem', color: DWA.goldInk }} />
                    Technical details
                    <span style={{ fontWeight: 400, color: DWA.textMuted }}> · Technical permission</span>
                  </button>
                  {showTech &&
                    page.technical.map((t) => (
                      <div key={t.file} style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: '#f6f6f4', borderRadius: DWA.radius }}>
                        <p style={{ margin: 0, fontSize: '0.72rem', color: DWA.textMuted }}>app/docs/knowledge-bank/{t.file}</p>
                        {renderMarkdown(t.body)}
                      </div>
                    ))}
                </section>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                {page.prev ? (
                  <button type="button" onClick={() => onOpen(page.prev!)} style={{ ...navButton, width: 'auto', color: DWA.goldInk, fontWeight: 700 }}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Previous
                  </button>
                ) : <span />}
                {page.next && (
                  <button type="button" onClick={() => onOpen(page.next!)} style={{ ...navButton, width: 'auto', color: DWA.goldInk, fontWeight: 700 }}>
                    Next <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                )}
              </div>
            </>
          )}
        </article>
      </div>
    </div>
  );
};
